import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, MessageCircle, Shield } from 'lucide-react';
import { useToast } from '@kollez/design-system';
import { useAuthStore } from '@/lib/authStore';
import { useCreateExport } from '@/lib/queries';

/** /settings — direct port of kollez/pages/shared/settings.html. */
type Section = 'profile' | 'notifications' | 'security' | 'data' | 'preferences';

const NOTIFICATIONS: Array<[string, boolean, boolean, boolean, boolean]> = [
  ['New drive posted', true, true, false, false],
  ['Interview scheduled', true, true, true, true],
  ['Offer released', true, true, true, true],
  ['Application update', true, false, false, false],
  ['Deadline reminder', true, false, true, false],
  ['Weekly summary', true, true, false, false],
];

// Fabricated aggregate — dropped (F-089). No sessions endpoint exists;
// the closest honest thing is the platform_admin "Sign out everywhere"
// action on /platform/users, which revokes every refresh-token family.

export function SettingsPage() {
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const [section, setSection] = useState<Section>('profile');
  const [notif, setNotif] = useState(NOTIFICATIONS);
  const [tfa, setTfa] = useState(true);
  const exportReq = useCreateExport();

  const NAV: Array<[Section, string]> = [
    ['profile', 'Profile'],
    ['notifications', 'Notifications'],
    ['security', 'Sign-in and security'],
    ['data', 'Your data'],
    ['preferences', 'Preferences'],
  ];

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Settings</h1>
          <p className="subhead">Your profile, how we reach you, and your data.</p>
        </div>
      </div>

      <div className="tabs show-mobile" role="tablist">
        {NAV.map(([k, l]) => <button key={k} className="tab" role="tab" aria-selected={section === k} onClick={() => setSection(k)}>{l}</button>)}
      </div>

      <div className="grid gap-6 grid-cols-[minmax(0,1fr)] min-[900px]:grid-cols-[200px_minmax(0,1fr)] items-start">
        <nav className="hide-mobile sticky grid gap-0.5" style={{ top: 76 }} aria-label="Settings sections">
          {NAV.map(([k, l]) => (
            <button key={k} onClick={() => setSection(k)} className={`text-left px-3 py-2 rounded text-sm ${section === k ? 'bg-bg-info text-accent font-medium' : 'hover:bg-bg-secondary'}`}>
              {l}
            </button>
          ))}
        </nav>

        <div className="grid gap-5 min-w-0">
          {section === 'profile' && (
            <section className="card">
              <div className="card-head"><h2 className="h-section">Profile</h2></div>
              <div className="card-pad grid gap-5">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="avatar avatar-xl">{user?.fullName?.slice(0, 2).toUpperCase() ?? 'SR'}</span>
                  <div className="grid gap-1.5">
                    <div className="flex gap-2">
                      <button className="btn btn-outline btn-sm"><Upload size={12} />Upload photo</button>
                      <button className="btn btn-ghost btn-sm">Remove</button>
                    </div>
                    <p className="helper">JPG or PNG, square, under 2 MB.</p>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-1.5"><span className="label">Full name</span><input className="input" defaultValue={user?.fullName ?? 'Srinivas Rao K.'} /></label>
                  <label className="grid gap-1.5"><span className="label">Preferred name</span><input className="input" placeholder="What we call you" /></label>
                  <label className="grid gap-1.5"><span className="label">Email</span><input className="input" type="email" defaultValue={user?.email ?? 'srinivas@git.edu.in'} /></label>
                  <label className="grid gap-1.5"><span className="label">Mobile</span><input className="input" defaultValue="+91 98480 22145" /></label>
                  <label className="grid gap-1.5"><span className="label">Role</span><input className="input" defaultValue="Placement officer" readOnly /></label>
                  <label className="grid gap-1.5"><span className="label">Language</span>
                    <select className="select"><option>English</option><option>English + Telugu</option><option>English + Hindi</option></select>
                  </label>
                </div>
                <div>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled
                    title="Profile-edit endpoint (name / role / language) ships in a follow-up. The fields above are placeholder-only today."
                  >
                    Save changes · soon
                  </button>
                </div>
              </div>
            </section>
          )}

          {section === 'notifications' && (
            <section className="card">
              <div className="card-head flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="h-section">Notifications</h2>
                  <p className="helper mt-0.5">Choose where each update reaches you. Security alerts always go to email.</p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <span className="switch"><input type="checkbox" defaultChecked /><span /></span>
                  Quiet hours, 10 pm – 7 am
                </label>
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Update</th>
                      <th className="text-center">In app</th>
                      <th className="text-center">Email</th>
                      <th className="text-center">WhatsApp</th>
                      <th className="text-center">SMS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notif.map((row, i) => (
                      <tr key={row[0]}>
                        <td>{row[0]}</td>
                        {[1, 2, 3, 4].map((c) => (
                          <td key={c} className="text-center">
                            <input
                              type="checkbox"
                              className="check"
                              checked={row[c as 1 | 2 | 3 | 4] as boolean}
                              onChange={(e) => setNotif((n) => n.map((r, j) => j === i ? [r[0], ...([1, 2, 3, 4] as const).map((cc) => cc === c ? e.target.checked : r[cc as 1 | 2 | 3 | 4] as boolean)] as typeof r : r))}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-foot">
                <div className="notice notice-info">
                  <MessageCircle size={14} />
                  <div>
                    WhatsApp messages go to <b>+91 98480 22145</b>.{' '}
                    <button className="link" type="button" onClick={() => setSection('profile')}>Change number</button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {section === 'security' && (
            <section className="card">
              <div className="card-head"><h2 className="h-section">Sign-in and security</h2></div>
              <div className="card-pad grid gap-5">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div><div className="font-medium">Password</div><p className="helper">Last changed 3 Aug 2026</p></div>
                  <Link className="btn btn-outline btn-sm" to="/forgot-password">Change password</Link>
                </div>
                <hr className="divider" />
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-medium">Two-step verification</div>
                    <p className="helper">A 6-digit code from your authenticator app at every new sign-in.</p>
                  </div>
                  <span className="switch">
                    <input type="checkbox" checked={tfa} onChange={(e) => setTfa(e.target.checked)} disabled title="Two-step toggle wiring ships when the /me/security/2fa endpoint lands" aria-label="Two-step verification" />
                    <span />
                  </span>
                </div>
                <hr className="divider" />
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="font-medium">Where you're signed in</div>
                    <button
                      className="btn btn-ghost btn-sm"
                      disabled
                      title="Session directory + sign-out endpoints land in a follow-up. If you need to be signed out everywhere, ask a platform admin (they can do it from /platform/users)."
                    >
                      Sign out other devices · soon
                    </button>
                  </div>
                  <p className="helper m-0">
                    Kollez doesn't yet list your other devices. Access + refresh tokens auto-rotate every 15 min / 7 days.
                  </p>
                </div>
              </div>
            </section>
          )}

          {section === 'data' && (
            <section className="card">
              <div className="card-head"><h2 className="h-section">Your data</h2></div>
              <div className="card-pad grid gap-5">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-medium">Download your data</div>
                    <p className="helper">A copy of your profile, applications and offers, in JSON and PDF. Ready within 24 hours.</p>
                  </div>
                  <button
                    className="btn btn-outline btn-sm"
                    disabled={exportReq.isPending}
                    onClick={() => {
                      exportReq.mutate(undefined, {
                        onSuccess: () => toast.show({ message: "Export queued. We'll email you when it's ready." }),
                        onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
                      });
                    }}
                  >
                    {exportReq.isPending ? 'Queuing…' : 'Request export'}
                  </button>
                </div>
                <hr className="divider" />
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-medium">Delete account</div>
                    <p className="helper">Your college keeps your placement outcome for NIRF (without contact details). Everything else is deleted after 60 days.</p>
                  </div>
                  <Link className="btn btn-outline btn-sm" to="/settings/privacy">Delete account</Link>
                </div>
              </div>
            </section>
          )}

          {section === 'preferences' && (
            <section className="card card-pad grid gap-4">
              <h2 className="h-section">Preferences</h2>
              <label className="grid gap-1.5"><span className="label">Theme</span>
                <select className="select"><option>System</option><option>Ruled (light)</option><option>Blackboard (dark)</option></select>
              </label>
              <label className="grid gap-1.5"><span className="label">Time zone</span>
                <select className="select"><option>Asia/Kolkata (IST)</option><option>UTC</option></select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="check" defaultChecked /> Show Nexie tips on empty pages
              </label>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
